<template>
  <div class="toolbar">
    <div class="toolbar-left">
      <my-pagination
        :currentPage="currentPage"
        :totalPages="totalPages"
        @pageChanged="onPageChanged"
      />
    </div>

    <div class="toolbar-right">
      <button
        class="btn-filter settings-toggle"
        :class="{ active: settingsOpen }"
        type="button"
        :disabled="disabled"
        :aria-expanded="settingsOpen"
        aria-controls="cars-table-settings"
        @click="settingsOpen = !settingsOpen"
      >
        Настройки таблицы
        <span class="accordion-chevron" :class="{ open: settingsOpen }" aria-hidden="true" />
      </button>
    </div>

    <transition name="settings-accordion">
      <div
        v-if="settingsOpen"
        id="cars-table-settings"
        class="settings-panel"
      >
        <div class="settings-heading">
          <div>
            <div class="settings-title">Столбцы таблицы</div>
            <div class="settings-hint">
              Перетаскивайте карточки или используйте стрелки для изменения порядка.
              Ширину колонок меняйте перетаскиванием границы в заголовке таблицы.
            </div>
          </div>
          <div class="settings-actions">
            <button class="btn-filter" type="button" @click="$emit('resetSettings')">
              Сбросить
            </button>
            <button class="btn-filter" type="button" @click="settingsOpen = false">
              Готово
            </button>
          </div>
        </div>

        <draggable
          class="settings-grid"
          v-model="settingsOrder"
          item-key="id"
          handle=".drag-handle"
          @change="emitOrder"
        >
          <template #item="{ element, index }">
            <div class="settings-row">
              <button
                class="drag-handle"
                type="button"
                :aria-label="`Перетащить столбец ${element.label}`"
                title="Перетащить"
              >
                <span class="drag-grip" aria-hidden="true" />
              </button>
              <label class="visibility-label">
                <input
                  type="checkbox"
                  :checked="isVisible(element.id)"
                  :disabled="isLastVisible(element.id)"
                  @change="toggleVisibility(element.id)"
                >
                <span>{{ element.label }}</span>
              </label>
              <button
                type="button"
                class="move-btn"
                :disabled="index === 0"
                aria-label="Выше"
                @click="move(index, -1)"
              >
                ↑
              </button>
              <button
                type="button"
                class="move-btn"
                :disabled="index === settingsOrder.length - 1"
                aria-label="Ниже"
                @click="move(index, 1)"
              >
                ↓
              </button>
            </div>
          </template>
        </draggable>
      </div>
    </transition>
  </div>
</template>

<script>
import { computed, ref, watch } from 'vue'
import draggable from 'vuedraggable'
import {
  CARS_COLUMN_IDS,
  CARS_COLUMN_LABELS,
} from '../carsState.mjs'

export default {
  name: 'CarsToolbar',
  components: { draggable },
  props: {
    totalPages: { type: Number, default: 1 },
    currentPage: { type: Number, default: 1 },
    columnOrder: { type: Array, default: () => CARS_COLUMN_IDS },
    columnVisibility: { type: Object, default: () => ({}) },
    disabled: { type: Boolean, default: false },
  },
  emits: [
    'pageChanged',
    'columnOrderChanged',
    'columnVisibilityChanged',
    'resetSettings',
  ],
  setup(props, { emit }) {
    const settingsOpen = ref(false)
    const settingsOrder = ref([])

    const onPageChanged = (page) => {
      if (props.disabled) return
      emit('pageChanged', page)
    }

    const syncOrder = () => {
      settingsOrder.value = props.columnOrder.map((id) => ({
        id,
        label: CARS_COLUMN_LABELS[id] || id,
      }))
    }

    watch(() => props.columnOrder, syncOrder, { immediate: true, deep: true })

    const emitOrder = () => {
      emit(
        'columnOrderChanged',
        settingsOrder.value.map((item) => item.id),
      )
    }

    const move = (index, direction) => {
      const target = index + direction
      if (target < 0 || target >= settingsOrder.value.length) return
      const next = [...settingsOrder.value]
      ;[next[index], next[target]] = [next[target], next[index]]
      settingsOrder.value = next
      emitOrder()
    }

    const isVisible = (id) => props.columnVisibility[id] !== false
    const visibleCount = computed(
      () => CARS_COLUMN_IDS.filter(isVisible).length,
    )
    const isLastVisible = (id) => isVisible(id) && visibleCount.value === 1

    const toggleVisibility = (id) => {
      if (isLastVisible(id)) return
      emit('columnVisibilityChanged', {
        ...props.columnVisibility,
        [id]: !isVisible(id),
      })
    }

    return {
      settingsOpen,
      settingsOrder,
      onPageChanged,
      emitOrder,
      move,
      isVisible,
      isLastVisible,
      toggleVisibility,
    }
  },
}
</script>

<style scoped>
.toolbar {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0 12px;
  flex-wrap: wrap;
  gap: 8px;
}

.toolbar-left {
  flex-shrink: 0;
}

.toolbar-right {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
}

.settings-panel {
  flex: 0 0 100%;
  width: 100%;
  box-sizing: border-box;
  padding: 12px;
  border: 1px solid #d9e0e8;
  border-radius: 6px;
  background: #f8fafc;
}

.settings-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.settings-title {
  font-weight: 700;
  color: #344054;
}

.settings-hint {
  margin-top: 2px;
  color: #667085;
  font-size: 12px;
}

.settings-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
  gap: 6px;
}

.settings-row {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  min-height: 36px;
  padding: 3px 6px;
  border: 1px solid #e1e6ed;
  border-radius: 5px;
  background: #fff;
}

.settings-row:hover {
  border-color: #b9c8da;
}

.drag-handle,
.move-btn {
  border: 0;
  background: transparent;
  cursor: pointer;
  color: #667085;
}

.drag-handle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 24px;
  width: 24px;
  height: 28px;
  padding: 0;
  cursor: grab;
}

.drag-handle:active {
  cursor: grabbing;
}

.drag-grip {
  width: 12px;
  height: 18px;
  background-image: radial-gradient(circle, currentColor 1.4px, transparent 1.6px);
  background-size: 6px 6px;
}

.drag-handle:hover {
  color: #007bff;
}

.move-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 28px;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 1px solid #d0d5dd;
  border-radius: 4px;
  background: #fff;
  font-size: 15px;
}

.move-btn:hover:not(:disabled) {
  border-color: #007bff;
  background: #eef6ff;
  color: #007bff;
}

.move-btn:disabled {
  opacity: 0.35;
  cursor: default;
}

.visibility-label {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
  cursor: pointer;
}

.visibility-label span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  user-select: none;
}

.settings-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.settings-toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.settings-toggle.active {
  background: #eef6ff;
}

.accordion-chevron {
  width: 7px;
  height: 7px;
  border-right: 2px solid currentColor;
  border-bottom: 2px solid currentColor;
  transform: translateY(-2px) rotate(45deg);
  transition: transform 160ms ease;
}

.accordion-chevron.open {
  transform: translateY(2px) rotate(225deg);
}

.settings-accordion-enter-active,
.settings-accordion-leave-active {
  transition: opacity 140ms ease, transform 140ms ease;
}

.settings-accordion-enter-from,
.settings-accordion-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

.btn-filter {
  padding: 4px 12px;
  border: 1px solid #007bff;
  border-radius: 4px;
  background: #fff;
  color: #007bff;
  font-size: 13px;
  cursor: pointer;
  height: 30px;
  box-sizing: border-box;
  white-space: nowrap;
}

.btn-filter:hover {
  background: #007bff;
  color: #fff;
}

.btn-filter:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

@media (max-width: 900px) {
  .toolbar-left,
  .toolbar-right {
    width: 100%;
  }

  .toolbar-right {
    justify-content: flex-start;
  }

  .settings-heading {
    align-items: stretch;
    flex-direction: column;
  }

  .settings-actions {
    justify-content: flex-start;
  }
}
</style>
